import React, {useState, useEffect, useRef, useCallback, useContext} from 'react'
import './CalendarRightSidebar.scss'
import {Grid} from '@material-ui/core'
import moment from 'moment'
import _ from 'lodash'
import useCalendarViewContext from '../../context/CalendarView'
import {ReservationContext} from '../../context/Reservation'
import {ReservationCalendarMainContext} from '../../context/ReservationCalendarMain'
import ClearIcon from '@material-ui/icons/Clear'
import LoadingIndicator from '../ReservationConfiguration/LoadingIcon'
import {SidebarEvent} from './SidebarEvent'
import useCalendarEvents from '../ReservationNewCalendar/useCalendarEvents'
import useReservationGanttContext from '../../context/ReservationGantt/ReservationGantt'
import {SET_DIRECTION, RESET} from '../../state/constants'

export default function CalendarRightSidebar({...root}) {
  const [nextPage, setNextPage] = useState(1)
  const [nextDate, setNextDate] = useState(moment())
  const [prevPage, setPrevPage] = useState(-1)
  const {prepareParamsForApi} = useCalendarEvents()
  const [isLoading, setIsLoading] = useState(false)
  const [toggleDirection, setToggleDirection] = useState(false)
  const refScroller = useRef()
  const {viewData, selectedViewGroup} = useCalendarViewContext()

  const {loadConfiguration} = useContext(ReservationContext)
  const {
    searchResult,
    searchCalendar,
    realSearch,
    setRealSearch,
    calendarView,
    navigatedDate,
    setNavigatedDate,
    sidebarDateClicked,
    setSidebarDateClicked,
    isSameDate,
    dateWiseData,
    setDateWiseData,
    searchCalendardispatch,
  } = useContext(ReservationCalendarMainContext)

  const {
    startDate,
    endDate,
    prevEndDate,
    direction,
    offset,
    isLastFutureRecord,
    isLastPastRecord,
  } = searchResult

  const dataLoading = searchResult.isLoading || isLoading

  const tryCallApi = useCallback(
    params => {
      if (
        !!!params.entity_id ||
        params.start_date === null ||
        params.end_date === null ||
        searchResult.isLoading
      )
        return
      searchCalendar(params.entity_id, params, viewData)
    },
    // eslint-disable-next-line
    [realSearch]
  )

  const handleVScroll = e => {
    if (
      Math.round(e.currentTarget.offsetHeight + e.currentTarget.scrollTop) >
        Math.round(e.currentTarget.scrollHeight) &&
      !sidebarDateClicked &&
      !isLastFutureRecord
    ) {
      setNextPage(nextPage + 1)
      setNextDate(nextDate.add(1, 'months'))
      searchCalendardispatch({type: SET_DIRECTION, value: 'future'})
      setToggleDirection(!toggleDirection)
    }
    if (
      Math.round(e.currentTarget.scrollTop) === 0 &&
      !searchResult.isLoading &&
      !sidebarDateClicked &&
      !isLastPastRecord
    ) {
      setPrevPage(prevPage - 1)
      setNextDate(nextDate.subtract(1, 'months'))
      searchCalendardispatch({type: SET_DIRECTION, value: 'past'})
      setToggleDirection(!toggleDirection)
    }
  }

  useEffect(() => {
    let startMonth = moment(startDate).format('YYYY-MM-DD')
    let endMonth = moment(endDate).format('YYYY-MM-DD')
    let prevMonth = moment(prevEndDate).format('YYYY-MM-DD')
    if (
      !_.isEmpty(viewData?.selectedView) &&
      !dataLoading &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      if (!sidebarDateClicked && dateWiseData.length !== 0) {
        setIsLoading(true)
        tryCallApi({
          ...prepareParamsForApi(),
          start_date: direction === 'past' ? prevMonth : startMonth,
          end_date: direction === 'past' ? prevMonth : endMonth,
          offset: searchResult.offset,
          limit: 50,
          direction,
          query: realSearch,
        })
        setIsLoading(false)
      }
    }
    // eslint-disable-next-line
  }, [direction, toggleDirection])

  useEffect(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      tryCallApi({
        ...prepareParamsForApi(),
        direction: 'future',
        offset: searchResult.offset,
        limit: 50,
        query: realSearch,
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
      })
    }
    //eslint-disable-next-line
  }, [realSearch])

  useEffect(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      ((calendarView === 'day') | offset | (calendarView === 'week') || calendarView === 'month')
    ) {
      tryCallApi({
        ...prepareParamsForApi(),
        offset: searchResult.offset,
        direction: 'past',
        limit: 50,
        query: realSearch,
        start_date: moment(prevEndDate).format('YYYY-MM-DD'),
        end_date: moment(startDate).format('YYYY-MM-DD'),
      })
    }
    //eslint-disable-next-line
  }, [realSearch])

  const groupSidebarData = dateWiseData => {
    let data = []
    if (sidebarDateClicked && dateWiseData.length > 0) {
      data = _.groupBy(dateWiseData, date => {
        if (isSameDate(date.start, date.end)) {
          return moment(date.start)
            .startOf('hour')
            .format('h:mm a')
        } else {
          return 'dayView'
        }
      })
    } else if (dateWiseData.length > 0) {
      data = _.groupBy(dateWiseData, date => date.start.format('MM-DD-YYYY'))
    }
    return data
  }

  const displayHeaderDate = date => {
    return sidebarDateClicked && date !== 'dayView' && !moment(date).isValid()
      ? date
      : sidebarDateClicked && date === 'dayView'
      ? ''
      : moment(date).format('MMMM Do, YYYY')
  }

  useEffect(() => {
    if (searchResult) {
      setDateWiseData(searchResult.data)
    }
  }, [searchResult, setDateWiseData])

  const closeSidebar = () => {
    setRealSearch('')
    setSidebarDateClicked(false)
  }

  const groupedData = groupSidebarData(dateWiseData)
  return (
    <div className="calendar-right-sidebar" {...root}>
      <div className="toolbar">
        {realSearch && !sidebarDateClicked ? (
          <div className="title m-0 p-0">{`Results ${dateWiseData.length || 0}`}</div>
        ) : (
          <div className="title m-0 p-0">
            {navigatedDate?.isValid ? navigatedDate?.format('MMMM Do, YYYY') : ''}
          </div>
        )}
        <div className="m-0 p-0 ">
          <ClearIcon className="clear-icon" onClick={() => closeSidebar()} />
        </div>
      </div>
      <div className="events ap-scrollbar" onScroll={handleVScroll} ref={refScroller}>
        <>
          {Object.keys(groupedData).map(dateKey => (
            <>
              {!sidebarDateClicked && (
                <p className="event-grp-date">{displayHeaderDate(dateKey)} </p>
              )}
              {groupedData[dateKey].map((dateObj, key) => (
                <>
                  <SidebarEvent
                    dateObj={dateObj}
                    key={key}
                    start_field="start"
                    end_field="end"
                    onClickEvent={root.onClickEvent}
                  />
                </>
              ))}
            </>
          ))}
          {dataLoading && <LoadingIndicator />}
          {!dataLoading && dateWiseData.length === 0 && <p>No results found!</p>}
        </>
      </div>
    </div>
  )
}
